import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useCreateUnitActivation, useGetUnitActivationById, useUpdateUnitActivation } from '@apps-common/apis/portal/unit-activations'
import { UnitActivationConfirmationForm } from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/components'
import { UnitActivationUnitForm } from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/custom-activation'
import {
  ModifyUnitActivationStep,
  stepDirection
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/Index'
import { VertSpacerXl } from '@apps-common/components/layout'
import { useGarbageCollector } from '@apps-common/contexts/GarbageContext'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { IActivationServiceRequestCreateModel, IActivationServiceRequestTemplateModel } from '@apps-common/types/portal/service-request'
import { IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'

import { afterActivationUpdate } from '../helpers/afterActivationUpdate'

interface IProps {
  unitActivationId?: number | null
  defaultUnit?: IUnitGenericDetailsModel
  template: IActivationServiceRequestTemplateModel
}

export const UnitActivationTemplateModifyView: React.FC<IProps> = ({ unitActivationId, defaultUnit, template }) => {
  const [step, setStep] = useState<ModifyUnitActivationStep>(ModifyUnitActivationStep.unit)
  const { navigateReplace } = useNavigation()
  const { purge } = useGarbageCollector()

  const isEditing = !Number.isNaN(unitActivationId)

  const moveStep = (direction: stepDirection) => {
    if (direction === 'forward') {
      if (step === ModifyUnitActivationStep.unit) {
        setStep(ModifyUnitActivationStep.confirm)

        return ModifyUnitActivationStep.confirm
      }
      if (step === ModifyUnitActivationStep.confirm) {
        return ModifyUnitActivationStep.submit
      }
    } else if (direction === 'backward') {
      if (step === ModifyUnitActivationStep.confirm) {
        setStep(ModifyUnitActivationStep.unit)

        return ModifyUnitActivationStep.unit
      }
    }

    return step
  }

  const {
    isLoading: activationIsLoading,
    isError: activationIsError,
    data: activation
  } = useGetUnitActivationById({
    config: { enabled: isEditing },
    id: unitActivationId || 0
  })

  const { mutate: createActivation, isPending: createActivationIsPending } = useCreateUnitActivation(true, (data) =>
    afterActivationUpdate(data, navigateReplace, purge)
  )
  const { mutate: updateActivation, isPending: updateActivationIsPending } = useUpdateUnitActivation(true, (data) =>
    afterActivationUpdate(data, navigateReplace, purge)
  )

  const methods = useForm<IActivationServiceRequestCreateModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })
  const { setValue, handleSubmit, reset } = methods

  const initFromTemplate = () => {
    if (template) {
      setValue('useLineSupervision', template.useLineSupervision)
      setValue('unitPlanId', template.unitPlanId)
      setValue('mCentreId', template.mCentreId)
      setValue('mCentre', template.mCentre)
      setValue('assignBureau', template.assignBureau)
      setValue('bureauId', template.assignBureau ? template.bureauId : '')
      setValue('bureau', template.assignBureau ? template.bureau : '')
      setValue('assignTech', template.assignTech)
      setValue('techId', template.assignTech ? template.techId : '')
      setValue('tech', template.assignTech ? template.tech : '')
    }
  }

  useEffect(() => {
    if (template) {
      initFromTemplate()
    }
  }, [template])

  useEffect(() => {
    if (activation) {
      reset(activation)
    }
  }, [activation])

  const onSubmit = async (values: IActivationServiceRequestCreateModel) => {
    const nextStep = moveStep('forward')

    if (nextStep !== ModifyUnitActivationStep.submit) return

    if (!isEditing) {
      createActivation({ model: values })
    } else {
      updateActivation({ model: values })
    }
  }

  const loading = isEditing && (activationIsLoading || activationIsError)
  const updating = createActivationIsPending || updateActivationIsPending

  return (
    <div className='max-w-[800px]'>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          {/* step 1 - unit */}
          <UnitActivationUnitForm
            stepNo={1}
            loading={loading}
            updating={updating}
            step={step}
            moveStep={moveStep}
            defaultUnit={defaultUnit}
            hideLineSupervision
          />
          <VertSpacerXl />
          {/* step 4 - confirm */}
          <UnitActivationConfirmationForm
            stepNo={2}
            loading={loading}
            updating={updating}
            step={step}
            moveStep={moveStep}
            templatePlanId={template?.unitPlanId}
          />
        </form>
      </FormProvider>
      <VertSpacerXl />
    </div>
  )
}
